import React, { useState } from "react";
import { Upload, X, FileText, Loader2, CheckCircle2 } from "lucide-react";
import PDFMerger from "pdf-merger-js/browser";

const PDFMergeUploader = ({onUpload}) => {
  const [files, setFiles] = useState([]);
  const [merging, setMerging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Filter only PDF files
    const pdfFiles = selectedFiles.filter(
      (file) => file.type === "application/pdf"
    );

    if (pdfFiles.length !== selectedFiles.length) {
      setError("Only PDF files are allowed");
      setTimeout(() => setError(""), 3000);
    }

    setFiles((prev) => [...prev, ...pdfFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
    setSuccess("");
  };

  const mergePDFs = async (pdfFiles) => {
    const merger = new PDFMerger();

    for (const file of pdfFiles) {
      await merger.add(file);
    }

    const mergedPdfBlob = await merger.saveAsBlob();
    return mergedPdfBlob;
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Please select at least one PDF file");
      return;
    }

    setError("");
    setSuccess("");
    setMerging(true);

    try {
      // Merge PDFs
      const mergedPdfBlob = await mergePDFs(files);

      const mergedPdfFile = new File([mergedPdfBlob], "merged.pdf", {
        type: "application/pdf",
      });

      setMerging(false);
      setUploading(true);

      await onUpload(mergedPdfFile);

    //   setSuccess("PDFs merged and uploaded successfully!");
      setFiles([]);
    } catch (err) {
      setError(err.message || "An error occurred during upload");
    } finally {
      setMerging(false);
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Upload PDF Documents
        </h2>

        {/* Upload Area */}
        <div className="mb-6">
          <label
            htmlFor="file-upload"
            className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className="w-10 h-10 text-gray-400 mb-3" />
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Click to upload</span> or drag
                and drop
              </p>
              <p className="text-xs text-gray-500">PDF files only</p>
            </div>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept=".pdf,application/pdf"
              multiple
              onChange={handleFileSelect}
              disabled={merging || uploading}
            />
          </label>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Selected Files ({files.length})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                    disabled={merging || uploading}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={files.length === 0 || merging || uploading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
        >
          {merging && (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Merging PDFs...
            </>
          )}
          {uploading && (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Uploading...
            </>
          )}
          {!merging && !uploading && (
            <>Upload {files.length > 1 ? `${files.length} PDFs` : ""}</>
          )}
        </button>

        {files.length > 1 && (
          <p className="text-xs text-gray-500 text-center mt-3">
            All {files.length} PDFs will be merged into a single document before
            uploading
          </p>
        )}
      </div>
    </div>
  );
};

export default PDFMergeUploader;
